import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import SectionTitle from "@/components/SectionTitle";
import { Flame, Copy, Activity, ArrowUpRight, ArrowDownRight, LineChart, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import Sparkline from "@/components/Sparkline";
import StockChartModal from "@/components/StockChartModal";
import AnalysisModal from "@/components/AnalysisModal";

const volaColor = (v) => {
  const s = (v || "").toLowerCase();
  if (s.includes("ekstrem")) return "text-bear";
  return "text-amber";
};

const scoreColor = (n) => (n >= 80 ? "text-bull" : n >= 70 ? "text-accent" : n >= 60 ? "text-amber" : "text-[var(--text-secondary)]");
const scoreRing = (n) => (n >= 80 ? "var(--bull)" : n >= 70 ? "var(--accent)" : n >= 60 ? "var(--amber)" : "var(--text-muted)");

const ScalpCard = ({ s, idx, onChart, onAnalyze }) => {
  const copy = () => {
    const txt = `SCALPING ${s.ticker} (Skor AI ${s.ai_score ?? "-"})\nBuy: ${s.buy_range}\nTarget: ${s.target} | Cut Loss: ${s.cut_loss}\nMomentum: ${s.momentum}\nNote: ${s.note}\n\n- FAS Trading`;
    navigator.clipboard.writeText(txt)
      .then(() => toast.success("Sinyal scalping disalin ke clipboard!"))
      .catch(() => toast.error("Gagal menyalin ke clipboard"));
  };
  const sc = s.ai_score ?? 0;
  return (
    <motion.div whileHover={{ y: -3 }} transition={{ duration: 0.15 }} className={`card p-4 relative ${s.top_pick ? "border-[var(--accent)]/50 ring-1 ring-[var(--accent)]/30" : ""}`} data-testid={`scalping-card-${idx}`}>
      {s.top_pick && <span className="absolute -top-2 left-4 text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[var(--accent)] text-[#07090E]" data-testid={`scalp-toppick-${idx}`}>★ Pilihan Utama</span>}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-heading text-xl font-extrabold">{s.ticker}</span>
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase ${volaColor(s.volatility)}`}>
              <Flame size={11} /> {s.volatility}
            </span>
          </div>
          <p className="text-[11px] text-[var(--text-muted)]">{s.name}</p>
        </div>
        <button onClick={copy} data-testid={`copy-scalp-${idx}`} className="p-1.5 rounded-md bg-[var(--surface-hover)] hover:bg-[var(--accent)]/20 text-[var(--text-secondary)] hover:text-accent transition-colors">
          <Copy size={13} />
        </button>
      </div>
      <div className="flex items-center gap-3 mb-3 p-2.5 rounded-lg bg-[var(--surface-base)] border border-[var(--border-subtle)]" data-testid={`scalp-aiscore-${idx}`}>
        <div className="relative w-12 h-12 shrink-0" style={{ background: `conic-gradient(${scoreRing(sc)} ${sc * 3.6}deg, var(--surface-hover) 0deg)`, borderRadius: "9999px" }}>
          <div className="absolute inset-[3px] rounded-full bg-[var(--surface-base)] flex items-center justify-center">
            <span className={`font-mono-data text-sm font-extrabold ${scoreColor(sc)}`}>{sc}</span>
          </div>
        </div>
        <div className="flex-1">
          <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide flex items-center gap-1"><Sparkles size={10} className="text-accent" /> Skor AI</p>
          <p className={`text-sm font-bold ${scoreColor(sc)}`}>{s.score_label || "-"}</p>
          <p className="text-[10px] text-[var(--text-muted)]">Peluang buy · dari 100</p>
        </div>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-[var(--text-muted)] text-xs">Buy Range</span>
          <span className="font-mono-data font-bold">{s.buy_range}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[var(--text-muted)] text-xs flex items-center gap-1"><ArrowUpRight size={12} className="text-bull" /> Target</span>
          <span className="font-mono-data font-bold text-bull">{s.target}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[var(--text-muted)] text-xs flex items-center gap-1"><ArrowDownRight size={12} className="text-bear" /> Cut Loss</span>
          <span className="font-mono-data font-bold text-bear">{s.cut_loss}</span>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-[var(--border-subtle)]">
        <Sparkline ticker={s.ticker} height={34} />
        <p className="text-[11px] text-accent font-mono-data mb-1 mt-1">{s.momentum}</p>
        <p className="text-[11px] text-[var(--text-secondary)] leading-relaxed">{s.note}</p>
        <button onClick={() => onChart(s.ticker, s.name)} data-testid={`chart-scalp-${idx}`} className="w-full mt-2 h-8 rounded-md bg-[var(--surface-hover)] border border-[var(--border)] text-xs font-semibold flex items-center justify-center gap-1.5 hover:border-[var(--accent)]/40 hover:text-accent transition-colors">
          <LineChart size={13} /> Grafik Detail
        </button>
        <button onClick={() => onAnalyze(s.ticker)} data-testid={`analyze-scalp-${idx}`} className="w-full mt-1.5 h-8 rounded-md bg-[var(--accent)]/12 border border-[var(--accent)]/30 text-accent text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-[var(--accent)]/20 transition-colors">
          <Sparkles size={13} /> Analisis AI
        </button>
      </div>
    </motion.div>
  );
};

export default function ScalpingSection() {
  const [scalping, setScalping] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [chart, setChart] = useState(null);
  const [analyze, setAnalyze] = useState(null);

  useEffect(() => {
    const fetchScalping = () =>
      api.get("/scalping").then((res) => { setScalping(res.data.items || []); setUpdatedAt(res.data.updated_at || null); }).catch(() => {});
    fetchScalping();
    const t = setInterval(fetchScalping, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const fmtUpdated = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" }) + " WIB"; }
    catch { return null; }
  };

  return (
    <section id="scalping" className="scroll-mt-20">
      <SectionTitle
        eyebrow="High Momentum · Berpotensi Naik"
        title="3 Saham Scalping Volatil"
        desc="Saham volatil dengan momentum bullish & lonjakan volume yang berpotensi naik — diperbarui otomatis tiap 1 jam saat jam bursa."
        icon={Flame}
      />
      {updatedAt && (
        <p className="text-xs text-[var(--text-muted)] -mt-2 mb-4" data-testid="scalping-updated-at">
          Diperbarui otomatis tiap 1 jam · terakhir {fmtUpdated(updatedAt)}
        </p>
      )}
      <div className="card p-3 mb-5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[var(--amber)]/12 flex items-center justify-center">
          <Activity size={18} className="text-amber" />
        </div>
        <div className="flex-1">
          <p className="text-xs text-[var(--text-muted)]">Volatility Pulse Meter</p>
          <div className="h-1.5 rounded-full bg-[var(--surface-base)] overflow-hidden mt-1">
            <div className="h-full w-[78%] bg-gradient-to-r from-[var(--amber)] to-[var(--bear)] animate-pulse" />
          </div>
        </div>
        <span className="font-mono-data text-sm font-bold text-amber">TINGGI</span>
      </div>
      {scalping ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {scalping.map((s, i) => <ScalpCard key={i} s={s} idx={i} onChart={(t, n) => setChart({ ticker: t, name: n })} onAnalyze={(t) => setAnalyze(t)} />)}
        </div>
      ) : (
        <div className="card p-8 text-center text-[var(--text-secondary)] text-sm">Memuat sinyal scalping...</div>
      )}
      {chart && <StockChartModal ticker={chart.ticker} name={chart.name} onClose={() => setChart(null)} />}
      {analyze && <AnalysisModal ticker={analyze} onClose={() => setAnalyze(null)} />}
    </section>
  );
}
